import type { Role as DBRoleType } from "@compass/prisma";
import { z } from "zod";
import { ThreadAuthor } from "./author.js";
import { Password } from "./password.js";
import { Post } from "./posts.js";
import { VALID_TIMEZONES } from "./timezone.js";
import { TipTapDocSchema } from "./tiptapDoc.js";

export { Password };

export const DBRole = {
    GUEST: "GUEST",
    USER: "USER",
    COMPASS_USER: "COMPASS_USER",
    COMPASS_SUPERUSER: "COMPASS_SUPERUSER",
    READONLY_USER: "READONLY_USER",
} as const satisfies { [key in DBRoleType]: key };

export const Role = z.enum([
    DBRole.GUEST,
    DBRole.USER,
    DBRole.COMPASS_USER,
    DBRole.COMPASS_SUPERUSER,
    DBRole.READONLY_USER,
]);

export const FriendRequestStatus = z.enum(["DECLINED", "ACCEPTED"]);

export const User = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    email: z.string().email(),
    avatarUrl: z.string().nullish(),
    role: Role,
    aboutMe: z.string().nullish(),
    aboutMeContentJson: TipTapDocSchema.nullable().optional(),
    interests: z.string().nullish(),
    organizationId: z.number().nullish(),
    organizationName: z.string().nullish(),
    isSocialEnabled: z.boolean(),
    isPrivateEnabled: z.boolean(),
    calendarTimeZone: z
        .string()
        .refine((tz) => VALID_TIMEZONES.includes(tz), { message: "Invalid timezone" })
        .nullish(),
});

export const UserWithFriendId = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    email: z.string().email(),
    avatarUrl: z.string().nullish(),
    role: Role,
    friendId: z.number(),
});

export const UserWithFriend = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    email: z.string().email(),
    isSocialEnabled: z.boolean(),
    isPrivateEnabled: z.boolean(),
    avatarUrl: z.string().nullish(),
    recivedFriendRequest: z.boolean().nullable(),
    sentFriendRequest: z.boolean().nullable(),
    role: Role,
    organizationId: z.number().nullish(),
    organizationName: z.string().nullish(),
});

export const FriendRequest = z.array(
    z.object({
        id: z.number(),
        receiverId: z.number().nullish(),
        senderId: z.number().nullish(),
        sender: z.array(User).nullish(),
        status: z.string().nullish(),
    }),
);

export const FriendList = z.array(
    z.object({
        id: z.number(),
        userId: z.number().nullish(),
        friendId: z.number().nullish(),
        user: z.array(User).nullish(),
    }),
);

export const PublicUser = User.omit({ email: true, role: true });

export const SocialProfile = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    initials: z.string().min(1).max(2),
    email: z.string().email(),
    aboutMe: z.string().nullish(),
    aboutMeContentJson: TipTapDocSchema.nullable().optional(),
    interests: z.string().nullish(),
    isSocialEnabled: z.boolean(),
    deletedAt: z.string().nullish(),
    avatarUrl: z.string().nullish(),
    posts: z.array(Post).nullable(),
    meta: z.object({
        bio: z.string(),
        organization: z.string(),
    }),
    postComments: z.array(
        z.object({
            id: z.number(),
            contentJson: TipTapDocSchema,
            createdAt: z.string(),
            updatedAt: z.string(),
            postId: z.number(),
            authorId: z.number(),
            deletedAt: z.string().nullable(),
            post: z.object({
                id: z.number(),
                createdAt: z.string(),
                title: z.string(),
                updatedAt: z.string(),
                authorId: z.number(),
                deletedAt: z.string().nullable(),
                author: ThreadAuthor,
            }),
            author: ThreadAuthor,
        }),
    ),
});

export const CurrentUser = User.extend({
    isCompassUser: z.boolean(),
    follows: z.array(User).nullable(),
    meta: z.object({
        bio: z.string(),
        organization: z.string(),
    }),
    friends: z.array(UserWithFriendId),
    sentFriendRequest: FriendRequest,
    receivedFriendRequests: FriendRequest,
    isLockedUntilReset: z.boolean().optional().default(false),
    twoFactorEnabled: z.boolean().optional().default(false),
    isSocialEnabled: z.boolean().optional().default(false),
    isPrivateEnabled: z.boolean().optional().default(true),
});

export const UserMetaDataSchema = z.object({
    bio: z.string().nullable(),
    organization: z.string().nullable(),
});

export const UserMeta = z.object({
    id: z.number(),
    key: z.string(),
    data: UserMetaDataSchema,
    userId: z.number(),
    user: CurrentUser,
});

export type UserMetaData = z.infer<typeof UserMetaDataSchema>;
